import React, { useState, useRef, useEffect } from 'react'
import { motion } from 'motion/react'
import { Feature, CheckinFeature, ManualOverrideType } from '../../api/client'
import ReferenceValueDisplay from '../ReferenceValueDisplay'
import { ReferenceSelector } from './ReferenceSelector'
import { ListEditor } from './ListEditor'
import { ReferenceListEditor } from './ReferenceListEditor'
import { EnumListEditor } from './EnumListEditor'
import LocationListEditor, { LocationRow } from './LocationListEditor'
import MapEditor, { MapRow } from './MapEditor'
import MapValueDisplay from '../MapValueDisplay'
import ListValueDisplay from '../ListValueDisplay'
import BoolToggle from './BoolToggle'
import { boolLabels, boolLabelFor } from '../../utils/boolFeature'

interface EnhancedFeatureTokenProps {
    feature: Feature
    featureData: CheckinFeature | CheckinFeature[] // Array for list types
    collectionItems: Record<string, any>
    onRemove: (featureId: number) => void
    onValueChange: (featureId: number, decimalValue?: number, freeformValue?: string, referenceValue?: number) => void
    onListChange?: (featureId: number, values: Array<{value: string | number}>) => void
    /** Distinct from `onListChange` because location rows carry a richer
     *  shape (Places ref vs. coords-only) than the value-only payload the
     *  other list editors use. */
    onLocationListChange?: (featureId: number, values: LocationRow[]) => void
    /** Map features emit one row per (key, value) pair, with both halves on
     *  every row. Distinct from the list/location handlers because the
     *  payload spans both sides of the row. */
    onMapChange?: (featureId: number, values: MapRow[]) => void
    /** Forwarded to `LocationListEditor` → `LocationPickerModal` so newly-
     *  materialized Places land in the page-level cache. */
    onCollectionItemReady?: (item: { id: number; data: Record<string, any> }) => void
    /** Notifies the parent when this chip enters or leaves a list-typed
     *  edit popover. The parent uses this to keep the chip mounted across
     *  empty-list mid-edits (deleting the last row would otherwise tear
     *  down the popover the user is still interacting with). Only fires
     *  for list edit modes — scalar/inline/single-reference edits don't
     *  empty the underlying row, so they don't need protection. */
    onListEditStateChange?: (featureId: number, editing: boolean) => void
}

export const EnhancedFeatureToken: React.FC<EnhancedFeatureTokenProps> = ({
    feature,
    featureData,
    collectionItems,
    onRemove,
    onValueChange,
    onListChange,
    onLocationListChange,
    onMapChange,
    onCollectionItemReady,
    onListEditStateChange,
}) => {
    const [editMode, setEditMode] = useState<'none' | 'inline' | 'reference' | 'list' | 'reference-list' | 'enum-list' | 'location-list' | 'map'>('none')
    const [editValue, setEditValue] = useState('')
    const [escapeCount, setEscapeCount] = useState(0)

    const inputRef = useRef<HTMLInputElement>(null)
    const tokenRef = useRef<HTMLDivElement>(null)
    
    const featureType = feature.config?.type || 'decimal'
    const isList = featureType === 'list'
    const isMap = featureType === 'map'
    const listType = feature.config?.params?.type
    const isBool = featureType === 'bool'
    
    // Normalize featureData to array for consistent handling
    const featureArray = Array.isArray(featureData) ? featureData : [featureData]
    const primaryFeature = featureArray[0]
    
    useEffect(() => {
        if (editMode === 'inline' && inputRef.current) {
            inputRef.current.focus()
        }
    }, [editMode])

    // The yes/no toggle has no input to blur, so a click anywhere else closes
    // it without changing the answer — same as dismissing a list popover.
    useEffect(() => {
        if (editMode !== 'inline' || !isBool) return
        const handleClickOutside = (event: MouseEvent) => {
            if (tokenRef.current && !tokenRef.current.contains(event.target as Node)) {
                stopEditing()
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [editMode, isBool])

    // Mirror the open/close of any list-typed edit mode up to the parent so
    // it can keep the chip mounted while the list is empty mid-edit. The
    // cleanup also fires on unmount, which would clear the parent's
    // editingFeatureId — that path only triggers if our mount disappears
    // for unrelated reasons, since the empty-list path keeps us mounted.
    useEffect(() => {
        const isListEdit =
            editMode === 'location-list' ||
            editMode === 'reference-list' ||
            editMode === 'enum-list' ||
            editMode === 'list' ||
            editMode === 'map'
        if (!isListEdit) return
        onListEditStateChange?.(feature.id, true)
        return () => onListEditStateChange?.(feature.id, false)
    }, [editMode, feature.id, onListEditStateChange])
    
    const startEditing = () => {
        if (isMap) {
            setEditMode('map')
        } else if (isList && listType === 'location') {
            setEditMode('location-list')
        } else if (isList && listType === 'reference') {
            setEditMode('reference-list')
        } else if (isList && listType === 'enum') {
            setEditMode('enum-list')
        } else if (isList && (listType === 'str' || listType === 'num' || listType === 'bool')) {
            setEditMode('list')
        } else if (featureType === 'reference') {
            setEditMode('reference')
        } else if (isBool) {
            // The toggle reads the stored value directly; no draft to seed.
            setEditMode('inline')
        } else {
            setEditMode('inline')
            // Set initial edit value
            if (featureType === 'decimal' || featureType === 'rating' || featureType === 'labeled_rating') {
                setEditValue(primaryFeature.decimal_value?.toString() || '')
            } else {
                setEditValue(primaryFeature.freeform_value || '')
            }
        }
    }
    
    const stopEditing = () => {
        setEditMode('none')
        setEditValue('')
        setEscapeCount(0)
    }
    
    const handleSave = () => {
        // The yes/no toggle commits on pick (`handleBoolPick`); it has no draft.
        if (editMode !== 'inline' || isBool) return
        
        if (featureType === 'decimal' || featureType === 'rating' || featureType === 'labeled_rating') {
            const numValue = parseFloat(editValue)
            if (!isNaN(numValue) || editValue === '') {
                onValueChange(
                    feature.id,
                    editValue === '' ? undefined : numValue,
                    undefined,
                    undefined
                )
            }
        } else {
            onValueChange(
                feature.id,
                undefined,
                editValue,
                undefined
            )
        }
        stopEditing()
    }
    
    const handleKeyDown = (e: React.KeyboardEvent) => {
        // Enter/Space on a focused yes/no segment is that button's own click.
        if (e.key === 'Enter' && isBool && editMode === 'inline') return
        if (e.key === 'Enter') {
            e.preventDefault()
            if (editMode === 'none') {
                startEditing()
            } else if (editMode === 'inline') {
                handleSave()
            }
        } else if (e.key === 'Escape') {
            e.preventDefault()
            if (editMode === 'none') return
            
            if (escapeCount === 0) {
                setEscapeCount(1)
                setTimeout(() => setEscapeCount(0), 2000)
            } else {
                stopEditing()
            }
        } else if (e.key === 'Tab') {
            // Let tab work normally for navigation
            if (editMode === 'inline') {
                handleSave()
            }
        }
    }
    
    // One deliberate pick sets the answer and closes the toggle. Picking the
    // answer already stored just closes it, so it doesn't count as an edit.
    const handleBoolPick = (value: 1 | 0) => {
        if (primaryFeature.decimal_value == null || Number(primaryFeature.decimal_value) !== value) {
            onValueChange(feature.id, value, undefined, undefined)
        }
        stopEditing()
    }

    const handleReferenceChange = (itemId?: number) => {
        onValueChange(feature.id, undefined, undefined, itemId)
        stopEditing()
    }
    
    // List editors fire `onChange` on every mutation (Apple-style). The
    // popover stays open until the user dismisses it via click-outside,
    // Esc, or its header ×, all of which route through `stopEditing`.
    const handleReferenceListChange = (values: Array<{value: number}>) => {
        if (onListChange) {
            onListChange(feature.id, values)
        }
    }

    const handleListChange = (values: Array<{value: string | number}>) => {
        if (onListChange) {
            onListChange(feature.id, values)
        }
    }
    
    // Display logic
    const getDisplayValue = () => {
        if (isMap) {
            // Map rows share a (checkin_id, feature_id); the chip lists the
            // first few pairs (and "and N more") through the shared
            // `MapValueDisplay` helper. The leading ": " in the chip text is
            // owned by `MapValueDisplay`, so callers should *not* re-prefix.
            // Filter out keyless placeholder rows (those exist while the user
            // is mid-edit on a freshly added feature) so the chip doesn't
            // flash "?" before any pair lands.
            const activeRows = featureArray.filter(
                cf => cf.manual_override !== ManualOverrideType.DELETE &&
                      (cf.key_freeform_value != null ||
                       cf.key_decimal_value != null ||
                       cf.key_reference_value != null),
            )
            if (activeRows.length === 0) return ''
            return (
                <MapValueDisplay
                    feature={feature}
                    rows={activeRows}
                    collectionItems={collectionItems}
                />
            )
        }
        if (isList) {
            // All list subtypes (str / num / enum / reference / location) go
            // through the shared `ListValueDisplay` helper so timeline chips
            // and composer tokens stay in lockstep — per-element truncation,
            // visible cap, and mobile "more" tail live in one file. Same
            // leading-": " contract as `MapValueDisplay` above, so the chip
            // text suppresses its own ": " prefix for lists.
            const activeRows = featureArray.filter(
                cf => cf.manual_override !== ManualOverrideType.DELETE,
            )
            if (activeRows.length === 0) return ''
            return (
                <ListValueDisplay
                    feature={feature}
                    rows={activeRows}
                    collectionItems={collectionItems}
                />
            )
        } else {
            // Single value types
            if (featureType === 'decimal') {
                const value = primaryFeature.decimal_value
                if (value !== null && value !== undefined) {
                    return feature.unit ? `${value}${feature.unit}` : `${value}`
                }
            } else if (featureType === 'rating') {
                const value = primaryFeature.decimal_value
                if (value !== null && value !== undefined) {
                    const max = feature.config?.params?.max || 5
                    return `${value}/${max}`
                }
            } else if (featureType === 'labeled_rating') {
                const value = primaryFeature.decimal_value
                if (value !== null && value !== undefined) {
                    const labels = feature.config?.params?.labels || []
                    const label = labels[Math.floor(value) - 1]
                    if (label) {
                        return label
                    } else {
                        const max = feature.config?.params?.max || 5
                        return `${value}/${max}`
                    }
                }
            } else if (isBool) {
                return boolLabelFor(primaryFeature.decimal_value, feature.config?.params) ?? ''
            } else if (featureType === 'reference') {
                if (primaryFeature.reference_value) {
                    return (
                        <ReferenceValueDisplay
                            itemId={primaryFeature.reference_value}
                            showPrefix={false}
                            collectionItems={collectionItems}
                        />
                    )
                }
            } else if (primaryFeature.freeform_value) {
                return primaryFeature.freeform_value
            }
        }
        return ''
    }
    
    const displayValue = getDisplayValue()
    const hasValue = displayValue !== ''
    const isManuallyOverridden = primaryFeature.manual_override === ManualOverrideType.KEEP_VALUE
    
    return (
        <div className="relative">
            <motion.div
                ref={tokenRef}
                initial={{ opacity: 0, scale: 0.6 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                whileHover={{ y: -1 }}
                whileTap={{ scale: 0.96 }}
                style={{ transformOrigin: 'left center' }}
                className="group flex items-center bg-amber-100 text-amber-900 rounded-full text-sm overflow-hidden focus-within:ring-2 focus-within:ring-action whitespace-nowrap"
                onKeyDown={handleKeyDown}
                tabIndex={0}
            >
                {editMode === 'inline' && isBool ? (
                    <div className="flex items-center gap-2 pl-3 pr-1 py-0.5">
                        <span>{feature.title}</span>
                        <BoolToggle
                            labels={boolLabels(feature.config?.params)}
                            value={primaryFeature.decimal_value}
                            onChange={handleBoolPick}
                            autoFocus
                        />
                    </div>
                ) : editMode === 'inline' ? (
                    <div className="flex items-center pl-3 pr-1 py-1 w-full">
                        {featureType === 'enum' ? (
                            <select
                                ref={inputRef as any}
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onBlur={handleSave}
                                className="bg-transparent border-none outline-none p-0 w-full"
                            >
                                <option value="">Select value...</option>
                                {(feature.config?.params?.values || []).map((value: any, index: number) => (
                                    <option key={index} value={value}>
                                        {value}
                                    </option>
                                ))}
                            </select>
                        ) : featureType === 'rating' || featureType === 'labeled_rating' ? (
                            <select
                                ref={inputRef as any}
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onBlur={handleSave}
                                className="bg-transparent border-none outline-none p-0 w-full"
                            >
                                <option value="">Select rating...</option>
                                {[...Array(feature.config?.params?.max || 5)].map((_, i) => {
                                    const rating = i + 1
                                    const label = featureType === 'labeled_rating' && feature.config?.params?.labels?.[i] 
                                        ? `${rating} - ${feature.config.params.labels[i]}`
                                        : rating.toString()
                                    return (
                                        <option key={rating} value={rating}>
                                            {label}
                                        </option>
                                    )
                                })}
                            </select>
                        ) : (
                            <input
                                ref={inputRef}
                                type={featureType === 'decimal' ? 'number' : 'text'}
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onBlur={handleSave}
                                className="bg-transparent border-none outline-none p-0 w-full"
                            />
                        )}
                        {featureType === 'decimal' && feature.unit && <span className="ml-1">{feature.unit}</span>}
                    </div>
                ) : (
                    <>
                        <div 
                            className="pl-3 py-1 cursor-pointer flex-1"
                            onClick={startEditing}
                        >
                            <span>
                                {feature.title}
                                {/* MapValueDisplay and ListValueDisplay both own their
                                    leading ": " (matches CheckinItem's chip), so
                                    suppress the default here for those branches. */}
                                {hasValue && !isMap && !isList && ': '}
                                {displayValue}
                            </span>
                        </div>
                        
                        {isManuallyOverridden && (
                            <>
                                <div className="h-6 mx-1 border-l border-amber-300"></div>
                                <div className="text-amber-800 pr-1" title="Manually set value">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                    </svg>
                                </div>
                            </>
                        )}
                        
                        <div className="h-6 mx-1 border-l border-amber-300"></div>
                        <button 
                            onClick={(e) => {
                                e.stopPropagation()
                                onRemove(feature.id)
                            }}
                            className="pr-2 py-1 text-amber-800 hover:text-amber-900"
                            tabIndex={-1}
                            aria-label={`Remove ${feature.title}`}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </>
                )}
            </motion.div>

            {/* Reference selector overlay */}
            {editMode === 'reference' && (
                <ReferenceSelector
                    collectionId={feature.config?.params?.collection_id || 0}
                    value={primaryFeature.reference_value || undefined}
                    onChange={handleReferenceChange}
                    onClose={stopEditing}
                />
            )}
            
            {/* List editor overlay */}
            {editMode === 'list' && (
                <ListEditor
                    feature={feature}
                    values={featureArray
                        .filter(cf => listType !== 'bool' || cf.decimal_value != null)
                        .map((cf, i) => ({
                            id: `item-${i}`,
                            value: listType === 'num' || listType === 'bool'
                                ? Number(cf.decimal_value || 0)
                                : cf.freeform_value || ''
                        }))}
                    choices={listType === 'bool' ? boolChoices(feature.config?.params) : undefined}
                    onChange={handleListChange}
                    onClose={stopEditing}
                />
            )}
            
            {/* Reference list editor overlay */}
            {editMode === 'reference-list' && (
                <ReferenceListEditor
                    feature={feature}
                    collectionItems={collectionItems}
                    values={featureArray
                        .filter(cf => cf.reference_value)
                        .map((cf, i) => ({
                            id: `item-${i}`,
                            value: cf.reference_value!
                        }))}
                    onChange={handleReferenceListChange}
                    onClose={stopEditing}
                />
            )}
            
            {/* Enum list editor overlay */}
            {editMode === 'enum-list' && (
                <EnumListEditor
                    feature={feature}
                    values={featureArray
                        .filter(cf => cf.freeform_value)
                        .map((cf, i) => ({
                            id: `item-${i}`,
                            value: cf.freeform_value!
                        }))}
                    onChange={handleListChange}
                    onClose={stopEditing}
                />
            )}

            {/* Map editor overlay */}
            {editMode === 'map' && (
                <MapEditor
                    feature={feature}
                    values={featureArray
                        .filter(cf =>
                            cf.manual_override !== ManualOverrideType.DELETE &&
                            (cf.key_freeform_value != null ||
                             cf.key_decimal_value != null ||
                             cf.key_reference_value != null)
                        )
                        .map((cf, i) => ({
                            id: `pair-${i}`,
                            key_freeform_value: cf.key_freeform_value ?? undefined,
                            key_decimal_value: cf.key_decimal_value ?? undefined,
                            key_reference_value: cf.key_reference_value ?? undefined,
                            freeform_value: cf.freeform_value ?? undefined,
                            decimal_value: cf.decimal_value ?? undefined,
                            reference_value: cf.reference_value ?? undefined,
                            latitude: cf.latitude ?? undefined,
                            longitude: cf.longitude ?? undefined,
                        }))}
                    collectionItems={collectionItems}
                    onCollectionItemReady={onCollectionItemReady}
                    onChange={(values) => {
                        if (onMapChange) {
                            onMapChange(feature.id, values)
                        }
                    }}
                    onClose={stopEditing}
                />
            )}

            {/* Location list editor overlay */}
            {editMode === 'location-list' && (
                <LocationListEditor
                    feature={feature}
                    values={featureArray
                        .filter(cf =>
                            cf.reference_value != null ||
                            (cf.latitude != null && cf.longitude != null)
                        )
                        .map((cf, i) => ({
                            id: `loc-${i}`,
                            referenceValue: cf.reference_value ?? undefined,
                            latitude: cf.latitude ?? undefined,
                            longitude: cf.longitude ?? undefined,
                        }))}
                    collectionItems={collectionItems}
                    onCollectionItemReady={onCollectionItemReady}
                    onChange={(values) => {
                        if (onLocationListChange) {
                            onLocationListChange(feature.id, values)
                        }
                    }}
                    onClose={stopEditing}
                />
            )}

            {escapeCount > 0 && (
                <div className="absolute top-full left-0 mt-1 text-xs text-orange-600 bg-white px-2 py-1 rounded shadow-sm border">
                    Press Esc again to cancel changes
                </div>
            )}
        </div>
    )
}
/** A list<bool>'s add buttons: the two answers, stored as 1 / 0. */
const boolChoices = (params: any) => {
    const [trueLabel, falseLabel] = boolLabels(params)
    return [
        { label: trueLabel, value: 1 },
        { label: falseLabel, value: 0 },
    ]
}
